import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, UserSettings } from '../types';
import { authApi, clearStoredToken, clearStoredRefreshToken, getStoredRefreshToken, type AuthResponse, type BackendSafeUser } from '../api/client';
import type { Tenant } from '../types';
import { queryClient, queryKeys } from '../hooks/useApi';
import { aiDashboardApi } from '../api/client';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isHydrating: boolean;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, businessName: string, promoCode?: string) => Promise<void>;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  updateSettings: (settings: Partial<UserSettings>) => void;
  completeOnboarding: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  hydrateFromServer: () => Promise<void>;
}

// Default settings for new users (backend doesn't store these yet)
const defaultSettings: UserSettings = {
  lowStockThreshold: 10,
  defaultBufferStock: 5,
  notificationsEnabled: true,
  emailAlerts: true,
  syncInterval: 5,
};

// Map backend user + tenant to frontend User type
function mapToFrontendUser(backendUser: BackendSafeUser, tenant: Tenant): User {
  return {
    id: backendUser.id,
    email: backendUser.email,
    name: backendUser.name || backendUser.email.split('@')[0],
    businessName: tenant.name,
    onboardingComplete: backendUser.onboardingComplete ?? false,
    role: backendUser.role,
    isSuperAdmin: backendUser.isSuperAdmin ?? false,
    settings: defaultSettings,
  };
}

// Helper to slugify business name for tenant slug
function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 100);
}

// Fire-and-forget prefetch of dashboard overview so data is cached
// by the time the user lands on the Dashboard page.
function prefetchDashboardOverview() {
  queryClient.prefetchQuery({
    queryKey: queryKeys.aiDashboardOverview,
    queryFn: () => aiDashboardApi.getOverview(),
    staleTime: 60_000,
  });
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      isHydrating: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true });

        try {
          const response: AuthResponse = await authApi.login({ email, password });

          const frontendUser = mapToFrontendUser(response.user, response.tenant);

          set({
            user: frontendUser,
            token: response.tokens.accessToken,
            isAuthenticated: true,
            isLoading: false,
          });
          prefetchDashboardOverview();
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (email: string, password: string, name: string, businessName: string, promoCode?: string) => {
        set({ isLoading: true });

        try {
          const response: AuthResponse = await authApi.register({
            tenantName: businessName,
            tenantSlug: slugify(businessName),
            email,
            password,
            name,
            ...(promoCode ? { promoCode } : {}),
          });

          const frontendUser = mapToFrontendUser(response.user, response.tenant);
          // New registrations always start with onboarding incomplete
          frontendUser.onboardingComplete = false;

          set({
            user: frontendUser,
            token: response.tokens.accessToken,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        clearStoredToken();
        clearStoredRefreshToken();
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
      },

      updateUser: (updates: Partial<User>) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...updates } });
        }
      },

      updateSettings: (settings: Partial<UserSettings>) => {
        const { user } = get();
        if (user) {
          set({
            user: {
              ...user,
              settings: { ...user.settings, ...settings },
            },
          });
        }
      },

      completeOnboarding: async () => {
        const { user } = get();
        if (user) {
          // Update local state immediately for responsive UI
          set({ user: { ...user, onboardingComplete: true } });
          // Persist to backend so it survives logout/login
          try {
            await authApi.completeOnboarding();
          } catch (err) {
            console.warn('Failed to persist onboarding status to backend:', err);
            // Local state is already updated, so user can proceed
          }
        }
      },

      refreshProfile: async () => {
        try {
          const response = await authApi.getProfile();
          const frontendUser = mapToFrontendUser(response.user as unknown as BackendSafeUser, response.tenant);
          set({ user: frontendUser });
        } catch (err: any) {
          if (err?.status === 401) {
            // Token expired — try refresh before clearing
            const storedRefresh = getStoredRefreshToken();
            if (storedRefresh) {
              try {
                const { accessToken } = await authApi.refreshToken(storedRefresh);
                set({ token: accessToken });
                // Retry profile fetch with new token
                const response = await authApi.getProfile();
                const frontendUser = mapToFrontendUser(response.user as unknown as BackendSafeUser, response.tenant);
                set({ user: frontendUser });
                return;
              } catch {
                // Refresh also failed — fall through to clear
              }
            }
            clearStoredToken();
            clearStoredRefreshToken();
            set({ user: null, token: null, isAuthenticated: false });
          }
          // Network errors — silently keep cached state
        }
      },

      hydrateFromServer: async () => {
        const { token, isAuthenticated } = get();
        if (!token || !isAuthenticated) return;
        set({ isHydrating: true });
        try {
          const response = await authApi.getProfile();
          const frontendUser = mapToFrontendUser(response.user as unknown as BackendSafeUser, response.tenant);
          set({ user: frontendUser, isHydrating: false });
          prefetchDashboardOverview();
        } catch (err: any) {
          if (err?.status === 401) {
            // Token expired — try refresh before clearing
            const storedRefresh = getStoredRefreshToken();
            if (storedRefresh) {
              try {
                const { accessToken } = await authApi.refreshToken(storedRefresh);
                set({ token: accessToken });
                const response = await authApi.getProfile();
                const frontendUser = mapToFrontendUser(response.user as unknown as BackendSafeUser, response.tenant);
                set({ user: frontendUser, isHydrating: false });
                prefetchDashboardOverview();
                return;
              } catch {
                // Refresh also failed — fall through to clear
              }
            }
            clearStoredToken();
            clearStoredRefreshToken();
            set({ user: null, token: null, isAuthenticated: false, isHydrating: false });
          } else {
            // Network errors — keep cached state (offline tolerance)
            set({ isHydrating: false });
          }
        }
      },
    }),
    {
      name: 'stockclerk-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
